import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Typography,
  Divider,
  Input,
  Button,
  Tag,
  Spin,
  notification,
} from "antd";
import { onValue, ref as dbRef, update } from "firebase/database";

import { sendPasswordResetEmail, verifyBeforeUpdateEmail } from "firebase/auth";
import { auth, rtdb } from "../../../firebase.config";
import { UserContext } from "../../../context/UserContext";
import useLogout from "../../../hooks/useLogout";

const { Title, Text, Link } = Typography;

function maskPhone(phone) {
  if (!phone) return "";
  const digits = phone.replace(/\D+/g, "");
  if (digits.length < 4) return phone;
  const last4 = digits.slice(-4);
  return `XXXXXXX${last4}`;
}

export default function LandlordSettingsPage() {
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const [formData, setFormData] = useState({ email: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [api, contextHolder] = notification.useNotification();
  const logout = useLogout();

  useEffect(() => {
    if (!user?.uid) return;
    const profileRef = dbRef(rtdb, `landlords/${user.uid}`);
    const unsub = onValue(
      profileRef,
      (snap) => {
        setFormData({
          email: user.email || "",
          phone: user.displayPhone || "",
        });
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [user?.uid, user?.email, user?.displayPhone]);

  const emailVerified = !!auth.currentUser?.emailVerified;
  const phoneNotVerified = !!auth.currentUser?.phoneNotVerified;

  const maskedPhone = useMemo(
    () => maskPhone(formData.phone),
    [formData.phone]
  );

  useEffect(() => {
    if (auth.currentUser) {
      auth.currentUser.reload().then(() => {
        if (auth.currentUser.email !== user.email) {
          update(dbRef(rtdb, `landlords/${user.uid}`), {
            email: auth.currentUser.email,
          });
          setUser({ ...user, email: auth.currentUser.email });
        }
      });
    }
  }, []);

  const handleEmailUpdate = async (newEmail) => {
    try {
      if (!auth.currentUser) throw new Error("Not signed in");

      if (newEmail === auth.currentUser.email) {
        return api.error({
          message: "Same email address",
          description:
            "You entered the same email that is already on your account.",
        });
      }

      await verifyBeforeUpdateEmail(auth.currentUser, newEmail);

      api.success({
        message: "Verification required",
        description: `We sent a verification link to ${newEmail}. 
        Please click it to confirm before your email is updated.
        You will be logged out shortly...`,
      });

      setTimeout(() => {
        logout();
      }, 3000);
    } catch (err) {
      console.log(err);
      if (err.code === "auth/requires-recent-login") {
        localStorage.setItem("pendingEmailUpdate", newEmail);
        await auth.signOut();
        navigate("/signin");
        return;
      }

      api.error({
        message: "Failed to update email",
        description: err?.message || String(err),
      });
    }
  };

  const handlePasswordReset = async () => {
    try {
      const email = formData.email || user?.email;
      if (!email) {
        api.error({
          message: "No email on file",
          description: "Cannot send password reset without an email address.",
        });
        return;
      }
      await sendPasswordResetEmail(auth, email);
      api.success({
        message: "Password reset sent",
        description: `We emailed a password reset link to ${email}.`,
      });
    } catch (err) {
      api.error({
        message: "Failed to send reset email",
        description: err?.message || String(err),
      });
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      {contextHolder}
      <Title level={3}>Account Settings</Title>

      {/* Email Section */}
      <Divider />
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={5}>
            Email <Text type="danger">*</Text>
          </Title>

          <Input
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            placeholder="Enter new email"
          />

          <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
            You’ll need to verify any new email address before it can be used.
          </Text>

          <div style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
            <Tag color={emailVerified ? "green" : "orange"}>
              {emailVerified ? "Verified" : "Not verified"}
            </Tag>

            <Button
              type="primary"
              disabled={
                !formData.email ||
                formData.email === user.email ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
              }
              style={{ marginLeft: "auto" }}
              onClick={() => handleEmailUpdate(formData.email.trim())}
            >
              Update Email
            </Button>
          </div>
        </Col>

        {/* Phone */}
        <Col span={24}>
          <Title level={5}>Phone Number</Title>
          <Text type="secondary" style={{ display: "block" }}>
            Keep your phone up to date to secure your account and receive
            important alerts.
          </Text>

          <div style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
            <Input
              disabled
              value={maskedPhone || "Not set"}
              style={{ width: 200 }}
            />
            <Button
              style={{ marginLeft: "auto" }}
              onClick={() => navigate("/landlord/phone-change")}
            >
              Change Phone
            </Button>
          </div>

          <Tag
            color={phoneNotVerified ? "green" : "orange"}
            style={{ marginTop: "12px" }}
          >
            {phoneNotVerified ? "Verified" : "Not verified"}
          </Tag>

          {/* Phone Verification */}
          {!phoneNotVerified && (
            <div style={{ marginTop: 16 }}>
              <Typography.Link
                onClick={() => navigate("/landlord/phone-verification")}
              >
                Verify your phone number
              </Typography.Link>
            </div>
          )}
        </Col>

        {/* Password Section */}
        <Col span={24}>
          <Title level={5}>Update Password</Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Change your password
          </Text>
          <div style={{ marginTop: 8, textAlign: "right" }}>
            <Button onClick={handlePasswordReset}>Update Password</Button>
          </div>
        </Col>
      </Row>
    </div>
  );
}
